import React, {
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import {
  EncounterSummaryViewModel,
} from 'dtos';
import Select from 'core/components/Select';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import RTMVisitLabel from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/RTM/RTMVisitLabel';
import {
  useGetEncountersForRTMRecommendation,
} from 'views/EMR/hooks/pcc';

type Props = {
  patientId: number;
  value?: number;
  onChange: (encounter: EncounterSummaryViewModel) => void;
};

const newEncounterOption: EncounterSummaryViewModel = {
  encounterId: 0,
};

export default function RTMEncounterSelect({
  patientId,
  value,
  onChange,
}: Props) {
  const {
    data: encounters = [],
    isFetching,
  } = useGetEncountersForRTMRecommendation(patientId);

  const options = useMemo(
    () => [newEncounterOption, ...encounters],
    [encounters],
  );

  const selectedEncounter = useMemo(() => (value === 0
    ? newEncounterOption
    : encounters.find((encounter) => encounter.encounterId === value)), [encounters, value]);

  const getLabel = useCallback((encounter: EncounterSummaryViewModel) => (
    <RTMVisitLabel visit={encounter} />
  ), []);

  useEffect(() => {
    if (isFetching || !isNil(value) || isEmpty(encounters)) {
      return;
    }

    onChange(head(encounters) as EncounterSummaryViewModel);
  }, [encounters, isFetching, onChange, value]);

  return (
    <Wrapper
      id="rtm-encounter-select"
      className="w-full"
      label="Encounter"
      required
      labelClasses="font-semibold text-xs text-gray-500"
    >
      <Select<EncounterSummaryViewModel>
        className="w-full"
        muiClasses={{
          select: '!text-left',
        }}
        id="rtm-encounter-select"
        value={selectedEncounter ?? null}
        options={options}
        getValue={(encounter) => encounter?.encounterId ?? ''}
        getLabel={getLabel}
        disabled={isFetching}
        onChange={(option) => onChange(option as EncounterSummaryViewModel)}
        placeholder="Select encounter"
        renderValue={() => (
          isNil(selectedEncounter)
            ? 'Select encounter'
            : <RTMVisitLabel visit={selectedEncounter} />
        )}
      />
    </Wrapper>
  );
}
